import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  createForm,
  getForm,
  updateForm,
  type FormField,
  type FormFieldType,
  type FormTriggerType,
} from '@/lib/api/forms.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const FIELD_TYPES: { type: FormFieldType; label: string }[] = [
  { type: 'text', label: 'Text' },
  { type: 'textarea', label: 'Textarea' },
  { type: 'number', label: 'Number' },
  { type: 'select', label: 'Select' },
  { type: 'radio', label: 'Radio' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'yes_no', label: 'Yes / No' },
  { type: 'rating', label: 'Rating' },
  { type: 'date', label: 'Date' },
  { type: 'signature', label: 'Signature' },
  { type: 'heading', label: 'Heading' },
  { type: 'divider', label: 'Divider' },
];

const TRIGGERS: { value: FormTriggerType; label: string }[] = [
  { value: 'manual', label: 'Manual (staff picks)' },
  { value: 'on_ticket_create', label: 'On ticket create' },
  { value: 'on_status_change', label: 'On status change' },
  { value: 'on_customer_create', label: 'On customer create' },
  { value: 'on_checkout', label: 'On checkout' },
];

function newFieldId() {
  return `field_${Math.random().toString(36).slice(2, 9)}`;
}

function isInputField(type: string) {
  return !['heading', 'divider'].includes(type);
}

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const formQ = useQuery({
    queryKey: ['custom-form', id],
    queryFn: () => getForm(id!),
    enabled: !isNew && Boolean(id),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<FormTriggerType>('manual');
  const [sendToDisplay, setSendToDisplay] = useState(false);
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!formQ.data) return;
    setName(formQ.data.name);
    setDescription(formQ.data.description ?? '');
    setTriggerType(formQ.data.triggerType);
    setSendToDisplay(Boolean(formQ.data.sendToDisplay));
    setFields([...formQ.data.fields].sort((a, b) => a.order - b.order));
  }, [formQ.data]);

  const selectedField = useMemo(
    () => fields.find((f) => f.id === selectedId) ?? null,
    [fields, selectedId],
  );

  const signatureCount = fields.filter((f) => f.type === 'signature').length;

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        triggerType,
        sendToDisplay,
        fields: fields.map((f, idx) => ({ ...f, order: idx })),
      };
      return isNew ? createForm(payload) : updateForm(id!, payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['custom-forms'] });
      queryClient.invalidateQueries({ queryKey: ['custom-form', saved.id] });
      toastSuccess(isNew ? 'Form created' : 'Form saved');
      if (isNew) navigate(`/settings/forms/${saved.id}/edit`, { replace: true });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function addField(type: FormFieldType) {
    if (type === 'signature' && signatureCount >= 1) {
      toastError('Most forms need only one signature field. Add it carefully if you need another.');
    }
    const field: FormField = {
      id: newFieldId(),
      type,
      label: type === 'heading' ? 'Section Title' : type === 'divider' ? 'Divider' : type === 'signature' ? 'Signature' : 'New Field',
      required: type === 'signature',
      order: fields.length,
      options: ['select', 'radio', 'checkbox'].includes(type) ? ['Option 1', 'Option 2'] : undefined,
      min: type === 'rating' ? 1 : undefined,
      max: type === 'rating' ? 5 : undefined,
    };
    setFields((prev) => [...prev, field]);
    setSelectedId(field.id);
  }

  function updateSelected(updates: Partial<FormField>) {
    if (!selectedId) return;
    setFields((prev) => prev.map((f) => (f.id === selectedId ? { ...f, ...updates } : f)));
  }

  function removeField(fieldId: string) {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    if (selectedId === fieldId) setSelectedId(null);
  }

  function moveField(fieldId: string, dir: -1 | 1) {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === fieldId);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next]!, copy[idx]!];
      return copy;
    });
  }

  if (!isNew && formQ.isLoading) {
    return <div className="rounded-2xl bg-white p-6 text-slate-500 shadow-sm">Loading form…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/settings/forms"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-950"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Forms
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-slate-950">{isNew ? 'New Form' : 'Edit Form'}</h1>
        </div>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!name.trim() || saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? 'Saving…' : 'Save Form'}
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Form name</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Description</span>
                <textarea
                  className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Trigger</span>
                <select
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value as FormTriggerType)}
                >
                  {TRIGGERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 pt-6 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={sendToDisplay}
                  onChange={(e) => setSendToDisplay(e.target.checked)}
                />
                Allow collect on customer display
              </label>
            </div>
            {signatureCount > 1 ? (
              <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                This form has {signatureCount} signature fields. Most customer forms only need one
                — extra pads often confuse customers.
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-800">Add fields</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {FIELD_TYPES.map((ft) => (
                <button
                  key={ft.type}
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => addField(ft.type)}
                >
                  <Plus className="h-3 w-3" />
                  {ft.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="mb-3 text-sm font-semibold text-slate-800">Form preview</p>
            <div className="space-y-3">
              {fields.map((field, idx) => (
                <div
                  key={field.id}
                  className={[
                    'rounded-xl border p-3',
                    selectedId === field.id ? 'border-primary bg-primary/5' : 'border-slate-200',
                  ].join(' ')}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setSelectedId(field.id)}
                  >
                    {field.type === 'heading' ? (
                      <p className="text-lg font-bold text-slate-950">{field.label}</p>
                    ) : field.type === 'divider' ? (
                      <hr className="border-slate-200" />
                    ) : field.type === 'signature' ? (
                      <>
                        <p className="text-sm font-medium text-slate-800">
                          {field.label}
                          {field.required ? <span className="text-red-500"> *</span> : null}
                        </p>
                        <div className="mt-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-8 text-center text-xs text-slate-400">
                          Signature pad (customer draws here)
                        </div>
                        {field.helpText ? (
                          <p className="mt-1 text-xs text-slate-500">{field.helpText}</p>
                        ) : (
                          <p className="mt-1 text-xs text-slate-400">Sign above</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-slate-800">
                          {field.label}
                          {field.required ? <span className="text-red-500"> *</span> : null}
                        </p>
                        <p className="text-xs capitalize text-slate-400">
                          {field.type.replace(/_/g, ' ')}
                        </p>
                      </>
                    )}
                  </button>
                  <div className="mt-2 flex gap-1">
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 p-1 disabled:opacity-40"
                      disabled={idx === 0}
                      onClick={() => moveField(field.id, -1)}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 p-1 disabled:opacity-40"
                      disabled={idx === fields.length - 1}
                      onClick={() => moveField(field.id, 1)}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-red-200 p-1 text-red-600"
                      onClick={() => removeField(field.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {!fields.length ? (
                <p className="text-sm text-slate-500">Add fields using the buttons above.</p>
              ) : null}
            </div>
          </div>
        </div>

        <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-950">Field settings</h2>
          {!selectedField ? (
            <p className="mt-3 text-sm text-slate-500">Select a field to edit its properties.</p>
          ) : (
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Label</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={selectedField.label}
                  onChange={(e) => updateSelected({ label: e.target.value })}
                />
              </label>
              {isInputField(selectedField.type) ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedField.required)}
                    onChange={(e) => updateSelected({ required: e.target.checked })}
                  />
                  Required
                </label>
              ) : null}
              {selectedField.type !== 'signature' &&
              selectedField.type !== 'heading' &&
              selectedField.type !== 'divider' ? (
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Placeholder</span>
                  <input
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    value={selectedField.placeholder ?? ''}
                    onChange={(e) => updateSelected({ placeholder: e.target.value || undefined })}
                  />
                </label>
              ) : null}
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Help text</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={selectedField.helpText ?? ''}
                  onChange={(e) => updateSelected({ helpText: e.target.value || undefined })}
                />
              </label>
              {selectedField.type === 'signature' ? (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Customers draw on a canvas. Touch, stylus, and mouse are supported. Submissions
                  store a PNG data URL with timestamp metadata.
                </p>
              ) : null}
              {['select', 'radio', 'checkbox'].includes(selectedField.type) ? (
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Options (one per line)</span>
                  <textarea
                    className="mt-1 min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    value={(selectedField.options ?? []).join('\n')}
                    onChange={(e) =>
                      updateSelected({
                        options: e.target.value
                          .split('\n')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </label>
              ) : null}
              {selectedField.type === 'number' || selectedField.type === 'rating' ? (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">Min</span>
                    <input
                      type="number"
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      value={selectedField.min ?? ''}
                      onChange={(e) =>
                        updateSelected({
                          min: e.target.value === '' ? undefined : Number(e.target.value),
                        })
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">Max</span>
                    <input
                      type="number"
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      value={selectedField.max ?? ''}
                      onChange={(e) =>
                        updateSelected({
                          max: e.target.value === '' ? undefined : Number(e.target.value),
                        })
                      }
                    />
                  </label>
                </div>
              ) : null}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
